/**
 * useDischargeSnippetStore — DISCHARGE-TEMPLATES Phase 1b Slice 1, FE-1.
 *
 * Per-(admission, field) cache of a doctor's discharge-summary templates
 * (§2.4). Same request-coalescing shape as `dentalHistoryStore.js`: a
 * module-level `inFlight` Map keyed OUTSIDE Zustand state so a promise
 * resolving never triggers a spurious render, and so N components racing
 * `load()` on the same tick produce exactly ONE network request (§2.10,
 * standing DoD "no request storm").
 *
 * No server cache exists on these routes (§2.10: "no pagination, no ETag").
 * `createSnippet`/`deleteSnippet` mutate the cached list LOCALLY on success
 * — never a refetch — per the FE-1 task spec ("local invalidation on
 * create/delete").
 *
 * Key = `${admissionId}::${field}` (`dischargeSnippetKey` below) — the
 * admission id is part of the key so switching admissions is a cache MISS
 * (a fresh `load()` fires); switching field within the same admission is
 * also a cache miss (ot_note and instructions never share an entry).
 *
 * No default "empty" entry is fabricated for an unloaded key — the
 * selector returns `undefined` until a `load()` resolves, so a consumer
 * can tell "not yet loaded" apart from "loaded, zero snippets" (never a
 * faked zero, matching the single-source-of-truth-derived-values rule).
 */
import { create } from 'zustand';

import {
  listDischargeSnippets,
  createDischargeSnippet,
  deleteDischargeSnippet,
} from '../services/api/dischargeSnippetApi';

/** Stable cache key — a string built from the same two primitives is
 * value-equal (`===`) across renders, so callers never need to memoise it. */
export const dischargeSnippetKey = (admissionId, field) => `${admissionId}::${field}`;

// Module-level, outside Zustand state — resolving a promise must not itself
// cause a re-render; only the `set()` calls inside it should.
const inFlight = new Map(); // key -> Promise<void>

const useDischargeSnippetStore = create((set, get) => ({
  /**
   * byKey[key]: {
   *   status: 'loading' | 'ready' | 'error',
   *   field, ownerDoctorName, limit, count, snippets: [],
   *   error: string | null, errorCode: string | null,
   * }
   */
  byKey: {},

  /**
   * Fetch a field's snippet list if not already cached.
   *  - status === 'ready'  -> no-op (cache hit)
   *  - in-flight promise   -> return that promise (request coalescing —
   *    two rapid opens of the same field fire exactly ONE request)
   *  - else                -> kick off a new fetch, registered as in-flight
   *    for the duration of the request only.
   */
  load: (admissionId, field) => {
    if (!admissionId || !field) return Promise.resolve();
    const key = dischargeSnippetKey(admissionId, field);
    const existing = get().byKey[key];
    if (existing?.status === 'ready') return Promise.resolve();
    const running = inFlight.get(key);
    if (running) return running;

    set((s) => ({
      byKey: {
        ...s.byKey,
        [key]: { ...(s.byKey[key] || {}), status: 'loading', error: null, errorCode: null },
      },
    }));

    const promise = (async () => {
      const res = await listDischargeSnippets(admissionId, field);
      if (res.success) {
        const { field: respField, owner_doctor_name, limit, count, snippets } = res.data;
        set((s) => ({
          byKey: {
            ...s.byKey,
            [key]: {
              status: 'ready',
              field: respField,
              ownerDoctorName: owner_doctor_name,
              limit,
              count,
              snippets,
              error: null,
              errorCode: null,
            },
          },
        }));
      } else {
        set((s) => ({
          byKey: {
            ...s.byKey,
            [key]: { ...(s.byKey[key] || {}), status: 'error', error: res.error, errorCode: res.code },
          },
        }));
      }
      inFlight.delete(key);
    })();
    inFlight.set(key, promise);
    return promise;
  },

  /**
   * Mark a field's cached list STALE so the next `load()` refetches it.
   *
   * The one case that needs this: item-mode fields (`snippet_mode: "item"`,
   * today `chief_complaints`) are learned SERVER-SIDE as a background side
   * effect of a successful summary save — nothing on the client creates
   * them, so `createSnippet`'s local prepend never sees them. Without an
   * invalidation, `load()` short-circuits on `status === 'ready'` and the
   * newly-learned complaints stay invisible until the panel remounts.
   *
   * Deliberately `'stale'` rather than dropping the entry: the previously
   * loaded rows stay on screen (no flash of an empty picker), and an
   * in-flight request is still coalesced by `inFlight`. The refetch happens
   * on the user's NEXT picker open — which also sidesteps the background
   * task's timing, since the server writes the rows while the response is
   * already on its way back to the browser.
   */
  invalidate: (admissionId, field) => {
    if (!admissionId || !field) return;
    const key = dischargeSnippetKey(admissionId, field);
    set((s) => {
      const prev = s.byKey[key];
      if (!prev) return s;
      return { byKey: { ...s.byKey, [key]: { ...prev, status: 'stale' } } };
    });
  },

  /**
   * Create a snippet and, on success, PREPEND it locally — no refetch (no
   * server cache to invalidate against, §2.10). Returns the API result so
   * the caller (`SaveAsTemplateModal`, FE-3) can show the coded error
   * inline (duplicate-title check is client-side per §2.5; a real 4xx from
   * the server, e.g. `SNIPPET_LIMIT_REACHED`, surfaces via `res.code`).
   */
  createSnippet: async (admissionId, field, { title, text }) => {
    const res = await createDischargeSnippet(admissionId, { field, title, text });
    if (res.success) {
      const key = dischargeSnippetKey(admissionId, field);
      set((s) => {
        const prev = s.byKey[key];
        if (!prev) return s;
        return {
          byKey: {
            ...s.byKey,
            [key]: {
              ...prev,
              snippets: [res.data, ...prev.snippets],
              count: prev.count + 1,
            },
          },
        };
      });
    }
    return res;
  },

  /**
   * Delete a snippet and, on success, REMOVE it locally by `snippet_id` —
   * no refetch.
   */
  deleteSnippet: async (admissionId, field, snippetId) => {
    const res = await deleteDischargeSnippet(admissionId, snippetId, field);
    if (res.success) {
      const key = dischargeSnippetKey(admissionId, field);
      set((s) => {
        const prev = s.byKey[key];
        if (!prev) return s;
        return {
          byKey: {
            ...s.byKey,
            [key]: {
              ...prev,
              snippets: prev.snippets.filter((sn) => sn.snippet_id !== snippetId),
              count: Math.max(0, prev.count - 1),
            },
          },
        };
      });
    }
    return res;
  },
}));

export default useDischargeSnippetStore;
